import { useEffect, useRef, useState } from "react";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useGetDownloadUrl } from "@src/packages/documents/hooks/useDocuments";

/**
 * Resolves a displayable URL for the patient's profile picture.
 *
 * The patient record only stores the upload id; its inline `url` comes back
 * null (same as document uploads), so we fetch a presigned download URL once
 * per upload id. Returns null while there is no picture or the URL is still
 * resolving, so callers can fall back to a placeholder.
 */
export function useProfilePictureUrl(): string | null {
  const { data: patient } = usePatient();
  const getDownloadUrl = useGetDownloadUrl();
  const [url, setUrl] = useState<string | null>(null);
  const hydratedIdRef = useRef<string | null>(null);

  const inlineUrl = patient?.profilePicture?.url ?? null;
  const uploadId = patient?.profilePicture?.id ?? null;

  useEffect(() => {
    if (inlineUrl) {
      setUrl(inlineUrl);
      return;
    }
    if (!uploadId) {
      setUrl(null);
      hydratedIdRef.current = null;
      return;
    }
    if (hydratedIdRef.current === uploadId) return;
    hydratedIdRef.current = uploadId;
    getDownloadUrl
      .mutateAsync(uploadId)
      .then((resolved) => {
        if (typeof resolved === "string") setUrl(resolved);
      })
      .catch(() => {
        hydratedIdRef.current = null;
      });
  }, [inlineUrl, uploadId, getDownloadUrl]);

  return url;
}
